import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { HomeHeroModel } from './buildHomeHero';

type HomeHeroProps = {
  firstName: string;
  hero: HomeHeroModel;
};

/** Same boundaries as the Overview greeting. */
function partOfDay(hour: number): 'morning' | 'afternoon' | 'evening' {
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}

/** Full-bleed hero — Ghost Screen - Home.dc.html (responsive). */
export function HomeHero({ firstName, hero }: HomeHeroProps) {
  const { t } = useTranslation('home');
  const greeting = t(`hero.greeting.${partOfDay(new Date().getHours())}`, { name: firstName });

  return (
    <section
      className="relative -mx-4 -mt-4 flex min-h-[200px] items-center overflow-hidden border-b sm:-mx-[clamp(18px,2.4vw,32px)] sm:-mt-5 sm:min-h-[236px]"
      style={{ background: 'var(--gs-panel)', borderColor: 'var(--gs-line)' }}
    >
      <img
        src="/hero-bg.png"
        alt=""
        className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-35 sm:inset-auto sm:end-0 sm:top-0 sm:w-[min(60%,760px)] sm:opacity-100"
        style={{ objectPosition: '70% 40%' }}
        draggable={false}
      />
      <div
        className="pointer-events-none absolute inset-0 sm:hidden"
        style={{
          background:
            'linear-gradient(180deg, color-mix(in srgb, var(--gs-panel) 88%, transparent) 0%, var(--gs-panel) 100%)',
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 hidden sm:block"
        style={{
          background:
            'linear-gradient(90deg, var(--gs-panel) 0%, var(--gs-panel) max(40%, calc(100% - min(60%, 760px))), transparent 78%)',
        }}
      />
      <div className="relative flex w-full min-w-0 flex-col items-start gap-2.5 px-4 py-8 sm:max-w-[min(760px,64%)] sm:px-[clamp(18px,2.4vw,32px)] sm:py-[34px]">
        <h1
          className="m-0 max-w-full truncate text-[clamp(24px,2.4vw,32px)] font-extrabold leading-tight tracking-[-0.025em]"
          style={{ color: 'var(--gs-ink)' }}
          title={greeting}
        >
          {greeting}
        </h1>
        <p className="m-0 flex max-w-[52ch] flex-col gap-1 text-pretty">
          <span className="text-[16px] font-bold sm:text-[17px]" style={{ color: 'var(--gs-ink)' }}>
            {hero.headline}
          </span>
          <span className="text-[15px] leading-relaxed" style={{ color: 'var(--gs-ink2)' }}>
            {hero.subline}
          </span>
        </p>
        {hero.action ? (
          <Link
            to={hero.action.to}
            className="mt-2 inline-flex items-center gap-2 rounded-xl px-[18px] py-3 text-[14.5px] font-bold no-underline transition hover:opacity-90"
            style={{ color: 'var(--gs-accent-ink)', background: 'var(--gs-accent-solid)' }}
          >
            {hero.action.label}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="rtl:-scale-x-100"
              aria-hidden
            >
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </Link>
        ) : null}
      </div>
    </section>
  );
}
